/** Start/end as ISO strings (local midnight, then toISOString), matching legacy insights date inputs. */
export type InsightsTimeRange = { start: string; end: string };

export function toISODate(d: Date): string {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString();
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** First local calendar day of the month containing `now` through local today (month-to-date). */
export function getMonthToDateRange(now: Date = new Date()): InsightsTimeRange {
  const y = now.getFullYear();
  const m = now.getMonth();
  const start = new Date(y, m, 1);
  const end = startOfLocalDay(now);
  return { start: toISODate(start), end: toISODate(end) };
}

/**
 * Rolling window of 30 inclusive calendar days ending on the local "today"
 * of `now`: start = local midnight 29 days before that day, end = local midnight that day.
 */
export function getLast30DaysRange(now: Date = new Date()): InsightsTimeRange {
  const end = startOfLocalDay(now);
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { start: toISODate(start), end: toISODate(end) };
}

export function getCurrentYearRange(now: Date = new Date()): InsightsTimeRange {
  const y = now.getFullYear();
  const start = new Date(y, 0, 1);
  const end = new Date(y, 11, 31);
  return { start: toISODate(start), end: toISODate(end) };
}

export function getLastYearRange(now: Date = new Date()): InsightsTimeRange {
  const y = now.getFullYear() - 1;
  const start = new Date(y, 0, 1);
  const end = new Date(y, 11, 31);
  return { start: toISODate(start), end: toISODate(end) };
}

export type InsightsTimeRangePresetId =
  | 'monthToDate'
  | 'last30Days'
  | 'currentYear'
  | 'lastYear';

export function getInsightsTimeRangePresets(now: Date = new Date()): Record<
  InsightsTimeRangePresetId,
  InsightsTimeRange
> {
  return {
    monthToDate: getMonthToDateRange(now),
    last30Days: getLast30DaysRange(now),
    currentYear: getCurrentYearRange(now),
    lastYear: getLastYearRange(now),
  };
}
